import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaEye,
  FaEyeSlash,
  FaSave,
  FaTrash,
  FaChevronDown,
  FaChevronRight,
} from "react-icons/fa";
import CommonModal from "Components/CommonModal";
import BlogEditor from "Components/Blog/BlogEditor";
import { catchError, commonModalSetting } from "JS/common";
import {
  getBlogCategories,
  getBlogDetail,
  createBlogPost,
  updateBlogPost,
  togglePublishBlog,
  deleteBlogPosts,
  uploadBlogImage,
} from "JS/API";
import {
  BLOG_DEFAULT_AUTHOR,
  BLOG_MAX_LENGTH,
  BLOG_SECTION_LABEL,
  BLOG_SECTION_OPTIONS,
  BLOG_SLUG_REGEX,
  BLOG_ERROR_MESSAGE,
  parseTags,
  stringifyTags,
  isQuillEmpty,
  normalizeErrorDetail,
} from "utils/blogConstants";
import { listPageStyles } from "utils/designSystem";

const EMPTY_FORM = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  thumbnail_url: "",
  category_key: "",
  tags_text: "",
  author_name: BLOG_DEFAULT_AUTHOR.author_name,
  author_role_label: BLOG_DEFAULT_AUTHOR.author_role_label,
  author_bio: BLOG_DEFAULT_AUTHOR.author_bio,
  is_featured: false,
  is_main: false,
};

const BlogEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [originalForm, setOriginalForm] = useState(null); // diff 용 (편집 모드)
  const [postStatus, setPostStatus] = useState("draft"); // 서버상의 현재 상태
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [authorOpen, setAuthorOpen] = useState(false);

  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({ mode: "", context: "", bool: false });
  // 모달 OK 클릭 시 실행될 콜백 (확인/알럿 모두 동일 경로)
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const thumbInputRef = useRef(null);

  // 확인 모달 (mode=confirm)
  const requestConfirm = (message, onConfirm) => {
    setPendingConfirm(() => onConfirm);
    commonModalSetting(setAlertBox, true, "confirm", message);
  };

  // 알럿 모달 (mode=alert) + 확인 누르면 후속 동작
  const requestAlert = (message, onConfirm) => {
    setPendingConfirm(onConfirm ? () => onConfirm : null);
    commonModalSetting(setAlertBox, true, "alert", message);
  };

  const styles = {
    container: listPageStyles.container,
    topBar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "20px",
      flexWrap: "wrap",
      gap: "12px",
    },
    backLink: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      color: "#374151",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: 500,
      background: "none",
      border: "none",
    },
    pageTitle: { fontSize: "22px", fontWeight: 700, color: "#0f172a", margin: 0 },
    layout: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) 320px",
      gap: "20px",
      alignItems: "flex-start",
    },
    card: {
      backgroundColor: "#fff",
      borderRadius: "12px",
      border: "1px solid #cbd5e1",
      boxShadow: "0 2px 4px -1px rgba(0,0,0,0.06)",
      padding: "20px",
      marginBottom: "16px",
    },
    label: {
      display: "block",
      fontSize: "13px",
      fontWeight: 600,
      color: "#374151",
      marginBottom: "6px",
    },
    required: { color: "#dc2626", marginLeft: "2px" },
    input: {
      width: "100%",
      padding: "10px 12px",
      border: "1px solid #cbd5e1",
      borderRadius: "6px",
      fontSize: "14px",
      color: "#0f172a",
      backgroundColor: "#fff",
      boxSizing: "border-box",
    },
    titleInput: {
      width: "100%",
      padding: "12px 14px",
      border: "1px solid #cbd5e1",
      borderRadius: "8px",
      fontSize: "20px",
      fontWeight: 600,
      color: "#0f172a",
      boxSizing: "border-box",
    },
    textarea: {
      width: "100%",
      padding: "10px 12px",
      border: "1px solid #cbd5e1",
      borderRadius: "6px",
      fontSize: "14px",
      color: "#0f172a",
      resize: "vertical",
      minHeight: "72px",
      fontFamily: "inherit",
      boxSizing: "border-box",
    },
    helper: { fontSize: "12px", color: "#6b7280", marginTop: "4px" },
    counter: { fontSize: "12px", color: "#9ca3af", marginTop: "4px", textAlign: "right" },
    sidebar: { position: "sticky", top: "24px" },
    sectionTitle: {
      fontSize: "13px",
      fontWeight: 700,
      color: "#0f172a",
      margin: "0 0 10px",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    },
    row: { marginBottom: "14px" },
    toggleRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 0",
      borderTop: "1px solid #f1f5f9",
    },
    buttonStack: { display: "flex", flexDirection: "column", gap: "8px", marginTop: "14px" },
    btnBase: {
      padding: "11px 14px",
      borderRadius: "6px",
      fontSize: "14px",
      fontWeight: 600,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      border: "none",
    },
    authorHeader: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      cursor: "pointer",
      userSelect: "none",
      marginBottom: authorOpen ? "14px" : "0",
      color: "#374151",
      fontSize: "14px",
      fontWeight: 600,
    },
    thumbRow: { display: "flex", gap: "6px" },
    thumbPreview: {
      marginTop: "8px",
      width: "100%",
      borderRadius: "6px",
      border: "1px solid #e2e8f0",
      overflow: "hidden",
      maxHeight: "160px",
    },
    statusLine: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 12px",
      backgroundColor: "#f8fafc",
      border: "1px solid #e2e8f0",
      borderRadius: "6px",
      fontSize: "13px",
      color: "#475569",
      marginBottom: "12px",
    },
  };

  // 버튼 variant (btnBase + 색상)
  const btnPrimary = { ...styles.btnBase, backgroundColor: "#2563eb", color: "#fff" };
  const btnSecondary = {
    ...styles.btnBase,
    backgroundColor: "#fff",
    color: "#374151",
    border: "1px solid #cbd5e1",
  };
  const btnDanger = {
    ...styles.btnBase,
    backgroundColor: "#fff",
    color: "#dc2626",
    border: "1px solid #fecaca",
  };
  const btnWarning = {
    ...styles.btnBase,
    backgroundColor: "#fef3c7",
    color: "#92400e",
    border: "1px solid #fde68a",
  };

  // 폼 변경 헬퍼
  const update = (column, value) => {
    setForm((prev) => ({ ...prev, [column]: value }));
    setDirty(true);
  };

  // 서버에서 받은 post 상세를 form에 반영 (로드/저장 성공 공용)
  const applyLoadedPost = (d) => {
    const loaded = {
      title: d.title || "",
      slug: d.slug || "",
      excerpt: d.excerpt || "",
      content: d.content || "",
      thumbnail_url: d.thumbnail_url || "",
      category_key: d.category?.key || "",
      tags_text: stringifyTags(d.tags || []),
      author_name: d.author_name || BLOG_DEFAULT_AUTHOR.author_name,
      author_role_label: d.author_role_label || BLOG_DEFAULT_AUTHOR.author_role_label,
      author_bio: d.author_bio || "",
      is_featured: !!d.is_featured,
      is_main: !!d.is_main,
    };
    setForm(loaded);
    setOriginalForm(loaded);
    setPostStatus(d.status || "draft");
  };

  // 카테고리 + (편집일 때) 상세 로드
  const loadInit = async () => {
    try {
      const catResult = await getBlogCategories();
      if (typeof catResult === "object" && catResult?.data?.data) {
        setCategories(catResult.data.data);
      } else {
        return handleBlogError(catResult);
      }
    } catch (err) {
      return handleBlogError(err);
    }

    if (!isEditMode) return;

    try {
      const result = await getBlogDetail(id);
      if (typeof result === "object" && result?.data?.data) {
        applyLoadedPost(result.data.data);
      } else {
        await handleBlogError(result);
      }
    } catch (err) {
      await handleBlogError(err);
    }
  };

  useEffect(() => {
    document.title = isEditMode
      ? "마크픽 관리자 > 블로그 편집"
      : "마크픽 관리자 > 블로그 작성";
    loadInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // 이탈 방지 (새로고침/창닫기)
  useEffect(() => {
    const handler = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // 클라이언트 검증
  const validate = (targetStatus) => {
    const title = form.title.trim();
    if (!title) return BLOG_ERROR_MESSAGE.titleRequired;
    if (title.length > BLOG_MAX_LENGTH.title) return BLOG_ERROR_MESSAGE.titleTooLong;
    if (form.excerpt.length > BLOG_MAX_LENGTH.excerpt) return BLOG_ERROR_MESSAGE.excerptTooLong;
    if (form.slug && !BLOG_SLUG_REGEX.test(form.slug)) return BLOG_ERROR_MESSAGE.slugInvalid;
    if (!form.category_key) return BLOG_ERROR_MESSAGE.categoryRequired;
    if (targetStatus === "published" && isQuillEmpty(form.content))
      return BLOG_ERROR_MESSAGE.contentRequired;
    return null;
  };

  // 생성/수정 공통 바디 빌더
  const buildCreateBody = (targetStatus) => {
    const body = {
      title: form.title.trim(),
      content: form.content,
      category_key: form.category_key,
      tags: parseTags(form.tags_text),
      status: targetStatus,
      is_featured: !!form.is_featured,
      is_main: !!form.is_main,
    };
    if (form.slug.trim()) body.slug = form.slug.trim();
    if (form.excerpt.trim()) body.excerpt = form.excerpt.trim();
    if (form.thumbnail_url.trim()) body.thumbnail_url = form.thumbnail_url.trim();
    if (form.author_name.trim()) body.author_name = form.author_name.trim();
    if (form.author_role_label.trim()) body.author_role_label = form.author_role_label.trim();
    if (form.author_bio.trim()) body.author_bio = form.author_bio.trim();
    return body;
  };

  // 변경된 필드만 포함하는 PATCH 바디 (수정)
  const buildPatchBody = (targetStatus) => {
    if (!originalForm) return {};
    const patch = {};
    const simpleFields = [
      "title",
      "slug",
      "excerpt",
      "content",
      "thumbnail_url",
      "category_key",
      "author_name",
      "author_role_label",
      "author_bio",
    ];
    simpleFields.forEach((f) => {
      if ((form[f] || "") !== (originalForm[f] || "")) {
        patch[f] = (form[f] || "").trim ? form[f].trim() : form[f];
      }
    });
    // is_featured, is_main
    ["is_featured", "is_main"].forEach((key) => {
      if (form[key] !== originalForm[key]) patch[key] = !!form[key];
    });
    // tags: 문자열이 달라졌으면 배열 전체 전송
    if (form.tags_text !== originalForm.tags_text) {
      patch.tags = parseTags(form.tags_text);
    }
    // 상태 전환
    if (targetStatus && targetStatus !== postStatus) {
      patch.status = targetStatus;
    }
    return patch;
  };

  const handleBlogError = async (err) => {
    if (typeof err === "string" || err === undefined) {
      // errorHandling 공통 코드 (auth/server)
      await catchError(err, navigate, setAlertBox, setAlert);
      return;
    }
    const status = err?.response?.status;
    const detailText = normalizeErrorDetail(err?.response?.data?.detail);
    if (status === 409) {
      requestAlert(BLOG_ERROR_MESSAGE.slugConflict);
    } else if (status === 404) {
      // 존재하지 않는 글 → 알림 후 목록으로 이동
      requestAlert(BLOG_ERROR_MESSAGE.notFound, () => navigate("/blog"));
    } else if (status === 403) {
      // 권한 부족 (code 300/400 외) — 백엔드 detail 그대로 전달
      requestAlert(detailText || "권한이 없습니다.");
    } else if (status === 400 || status === 422) {
      requestAlert(detailText || "요청이 올바르지 않습니다.");
    } else {
      requestAlert(BLOG_ERROR_MESSAGE.serverError);
    }
  };

  // 저장 (임시저장/발행 공통)
  const doSave = async (targetStatus) => {
    const error = validate(targetStatus);
    if (error) return requestAlert(error);
    if (saving) return;
    setSaving(true);
    try {
      const result = isEditMode
        ? await updateBlogPost(id, buildPatchBody(targetStatus))
        : await createBlogPost(buildCreateBody(targetStatus));

      if (typeof result === "object" && result?.data?.data) {
        const saved = result.data.data;
        setDirty(false);
        const isNewPost = !isEditMode;
        // 편집모드: 서버가 sanitize/id주입한 최신 상태를 form에 반영
        if (!isNewPost) applyLoadedPost(saved);
        const message =
          targetStatus === "published" ? "발행되었습니다." : "임시저장 되었습니다.";
        // 새 글은 상세 URL로 이동, 편집은 제자리 유지 (이미 form 리시드됨)
        requestAlert(message, isNewPost ? () => navigate(`/blog/${saved.id}`) : null);
      } else {
        await handleBlogError(result);
      }
    } catch (err) {
      await handleBlogError(err);
    } finally {
      setSaving(false);
    }
  };

  // 발행 취소 (토글 API)
  const doUnpublish = () => {
    requestConfirm(
      "이 글의 발행을 취소하시겠습니까?<br/>사이트에서 즉시 숨김 처리됩니다.",
      runUnpublish
    );
  };

  const runUnpublish = async () => {
    try {
      const result = await togglePublishBlog(id, false);
      if (typeof result === "object" && result?.data?.data) {
        applyLoadedPost(result.data.data);
        requestAlert("초안으로 변경되었습니다.");
      } else {
        await handleBlogError(result);
      }
    } catch (err) {
      await handleBlogError(err);
    }
  };

  // 삭제
  const doDelete = () => {
    requestConfirm(
      "이 게시글을 삭제하시겠습니까?<br/>삭제된 글은 복구할 수 없습니다.",
      runDelete
    );
  };

  const runDelete = async () => {
    try {
      const result = await deleteBlogPosts([Number(id)]);
      if (typeof result === "object" && result?.data) {
        setDirty(false);
        requestAlert("삭제되었습니다.", () => navigate("/blog"));
      } else {
        await handleBlogError(result);
      }
    } catch (err) {
      await handleBlogError(err);
    }
  };

  // 썸네일 업로드 (이미지 업로드 API 재사용)
  const onThumbnailUpload = async (file) => {
    if (!file) return;
    try {
      const result = await uploadBlogImage(file);
      if (typeof result === "object" && result?.data?.data?.url) {
        update("thumbnail_url", result.data.data.url);
      } else {
        await handleBlogError(result);
      }
    } catch (err) {
      await handleBlogError(err);
    }
  };

  // 뒤로 가기 (미저장 변경 확인)
  const onGoBack = () => {
    if (!dirty) return navigate("/blog");
    requestConfirm(
      "저장하지 않은 변경사항이 있습니다.<br/>정말 목록으로 돌아가시겠습니까?",
      () => {
        setDirty(false);
        navigate("/blog");
      }
    );
  };

  // 모달 확인 버튼 — 등록된 콜백 단일 실행
  const onModalOk = () => {
    if (alert === "logout") return navigate("/");
    const fn = pendingConfirm;
    setPendingConfirm(null);
    fn?.();
  };

  const titleByteLen = form.title.length;
  const excerptByteLen = form.excerpt.length;

  // 구분별로 묶되, 그룹 기준은 카테고리에 실제로 들어 있는 section 값으로 잡는다.
  // 상수에 없는 구분이 생겨도 셀렉트에서 통째로 빠지지 않게 하기 위함.
  const categoryGroups = useMemo(() => {
    const known = BLOG_SECTION_OPTIONS.filter((sec) => sec.value !== "all").map(
      (sec) => sec.value
    );
    const seen = [];
    categories.forEach((c) => {
      const sec = c.section || "blog";
      if (!seen.includes(sec)) seen.push(sec);
    });
    const ordered = [
      ...known.filter((sec) => seen.includes(sec)),
      ...seen.filter((sec) => !known.includes(sec)),
    ];
    return ordered.map((sec) => ({
      label: BLOG_SECTION_LABEL[sec] || sec,
      options: categories
        .filter((c) => (c.section || "blog") === sec)
        .map((c) => ({
          value: c.key,
          label: c.is_active === false ? `${c.name} (비노출)` : c.name,
          // 비노출 카테고리는 새로 고를 수 없고, 이미 그 카테고리에 있는 글만 유지할 수 있다
          disabled: c.is_active === false && c.key !== originalForm?.category_key,
        })),
    }));
  }, [categories, originalForm]);

  return (
    <div style={styles.container}>
      <div style={styles.topBar}>
        <button style={styles.backLink} onClick={onGoBack}>
          <FaArrowLeft />
          목록으로
        </button>
        <h1 style={styles.pageTitle}>
          {isEditMode ? "블로그 글 편집" : "블로그 글 작성"}
        </h1>
        <div style={{ width: "80px" }} />
      </div>

      <div style={styles.layout} className="blog-edit-layout">
        <div>
          <div style={styles.card}>
            {isEditMode && (
              <div style={styles.statusLine}>
                현재 상태:
                <strong style={{ color: postStatus === "published" ? "#059669" : "#64748b" }}>
                  {postStatus === "published" ? "발행" : "초안"}
                </strong>
              </div>
            )}

            <div style={styles.row}>
              <label style={styles.label}>
                제목<span style={styles.required}>*</span>
              </label>
              <input
                type="text"
                style={styles.titleInput}
                value={form.title}
                maxLength={BLOG_MAX_LENGTH.title}
                onChange={(e) => update("title", e.target.value)}
                placeholder="제목을 입력하세요"
              />
              <div style={styles.counter}>
                {titleByteLen} / {BLOG_MAX_LENGTH.title}
              </div>
            </div>

            <div style={styles.row}>
              <label style={styles.label}>slug</label>
              <input
                type="text"
                style={styles.input}
                value={form.slug}
                onChange={(e) => update("slug", e.target.value)}
                placeholder="비우면 제목으로 자동 생성됩니다"
              />
              <div style={styles.helper}>한글/영문/숫자/하이픈(-)만 사용 가능</div>
            </div>

            <div style={styles.row}>
              <label style={styles.label}>
                본문<span style={styles.required}>*</span>
              </label>
              <BlogEditor
                value={form.content}
                onChange={(html) => update("content", html)}
                onImageError={(msg) => requestAlert(msg)}
                minHeight="500px"
              />
              <div style={styles.helper}>
                이미지/링크/표/YouTube embed 등은 저장 시 자동으로 안전 처리됩니다.
              </div>
            </div>

            <div style={styles.row}>
              <label style={styles.label}>요약 (excerpt)</label>
              <textarea
                style={styles.textarea}
                value={form.excerpt}
                maxLength={BLOG_MAX_LENGTH.excerpt}
                onChange={(e) => update("excerpt", e.target.value)}
                placeholder="검색 결과/리스트에 노출될 한두 줄 요약"
              />
              <div style={styles.counter}>
                {excerptByteLen} / {BLOG_MAX_LENGTH.excerpt}
              </div>
            </div>
          </div>

          <div style={styles.card}>
            <div
              style={styles.authorHeader}
              onClick={() => setAuthorOpen((v) => !v)}
            >
              {authorOpen ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
              작성자 정보 (기본값: 마크픽)
            </div>
            {authorOpen && (
              <>
                <div style={styles.row}>
                  <label style={styles.label}>작성자 이름</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={form.author_name}
                    onChange={(e) => update("author_name", e.target.value)}
                  />
                </div>
                <div style={styles.row}>
                  <label style={styles.label}>작성자 역할</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={form.author_role_label}
                    onChange={(e) => update("author_role_label", e.target.value)}
                  />
                </div>
                <div style={styles.row}>
                  <label style={styles.label}>작성자 소개</label>
                  <textarea
                    style={styles.textarea}
                    value={form.author_bio}
                    onChange={(e) => update("author_bio", e.target.value)}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <aside style={styles.sidebar}>
          <div style={styles.card}>
            <div style={styles.sectionTitle}>분류</div>

            <div style={styles.row}>
              <label style={styles.label}>
                카테고리<span style={styles.required}>*</span>
              </label>
              <select
                style={styles.input}
                value={form.category_key}
                onChange={(e) => update("category_key", e.target.value)}
              >
                <option value="">카테고리 선택</option>
                {categoryGroups.map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.options.map((o) => (
                      <option key={o.value} value={o.value} disabled={o.disabled}>
                        {o.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div style={styles.row}>
              <label style={styles.label}>태그</label>
              <input
                type="text"
                style={styles.input}
                value={form.tags_text}
                onChange={(e) => update("tags_text", e.target.value)}
                placeholder="콤마(,)로 구분하여 입력"
              />
              <div style={styles.helper}>예: 상표출원, 거절이유, 사례</div>
            </div>

            <div style={styles.row}>
              <label style={styles.label}>썸네일 이미지</label>
              <div style={styles.thumbRow}>
                <input
                  type="text"
                  style={styles.input}
                  value={form.thumbnail_url}
                  onChange={(e) => update("thumbnail_url", e.target.value)}
                  placeholder="URL 직접 입력 또는 업로드"
                />
              </div>
              <div style={{ ...styles.thumbRow, marginTop: "6px" }}>
                <button
                  type="button"
                  style={{ ...btnSecondary, flex: 1 }}
                  onClick={() => thumbInputRef.current?.click()}
                >
                  이미지 업로드
                </button>
                {form.thumbnail_url && (
                  <button
                    type="button"
                    style={{ ...btnSecondary, width: "72px" }}
                    onClick={() => update("thumbnail_url", "")}
                  >
                    제거
                  </button>
                )}
                <input
                  ref={thumbInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    onThumbnailUpload(f);
                    e.target.value = "";
                  }}
                />
              </div>
              {form.thumbnail_url && (
                <div style={styles.thumbPreview}>
                  <img
                    src={form.thumbnail_url}
                    alt="썸네일 미리보기"
                    style={{ width: "100%", display: "block" }}
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                </div>
              )}
            </div>

            <div style={styles.toggleRow}>
              <div>
                <div style={{ fontSize: "14px", fontWeight: 600, color: "#374151" }}>전체 대표</div>
                <div style={styles.helper}>
                  전체 탭 첫 칸에 PICK 표시와 함께 노출됩니다. 구역당 1개만 지정할 수 있고, 새로 지정하면 기존 대표글은 해제됩니다.
                </div>
              </div>
              <input
                type="checkbox"
                checked={form.is_main}
                onChange={(e) => update("is_main", e.target.checked)}
                style={{ width: "18px", height: "18px", cursor: "pointer" }}
              />
            </div>

            <div style={styles.toggleRow}>
              <div>
                <div style={{ fontSize: "14px", fontWeight: 600, color: "#374151" }}>카테고리 추천</div>
                <div style={styles.helper}>
                  카테고리 목록 맨 앞에 고정됩니다. 카테고리당 1개만 지정할 수 있고, 새로 지정하면 기존 추천글은 해제됩니다.
                </div>
              </div>
              <input
                type="checkbox"
                checked={form.is_featured}
                onChange={(e) => update("is_featured", e.target.checked)}
                style={{ width: "18px", height: "18px", cursor: "pointer" }}
              />
            </div>

            <div style={styles.buttonStack}>
              <button
                type="button"
                style={btnSecondary}
                onClick={() => doSave("draft")}
                disabled={saving}
              >
                <FaSave />
                임시저장
              </button>
              <button
                type="button"
                style={btnPrimary}
                onClick={() => doSave("published")}
                disabled={saving}
              >
                <FaEye />
                {postStatus === "published" && isEditMode ? "발행 내용 수정" : "발행하기"}
              </button>
              {isEditMode && postStatus === "published" && (
                <button
                  type="button"
                  style={btnWarning}
                  onClick={doUnpublish}
                  disabled={saving}
                >
                  <FaEyeSlash />
                  발행 취소
                </button>
              )}
              {isEditMode && (
                <button
                  type="button"
                  style={btnDanger}
                  onClick={doDelete}
                  disabled={saving}
                >
                  <FaTrash />
                  삭제
                </button>
              )}
            </div>
          </div>
        </aside>
      </div>

      {alertBox.bool && (
        <CommonModal setModal={setAlertBox} modal={alertBox} okFn={onModalOk} />
      )}

      <style>{`
        @media (max-width: 960px) {
          .blog-edit-layout {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default BlogEdit;
